import React, { ReactNode } from "react";

import MainPage from "./MainPage";
import AboutPage from "./AboutPage";
import BusinessPage from "./BusinessPage";
import BusinessExpansionPage from "./BusinessExpansionPage";
import HowCreateOrderPage from "./HowCreateOrderPage";
import HowCreateAutoOrderPage from "./HowCreateAutoOrderPage";
import HowCreateConsolidatedOrderPage from "./HowCreateConsolidatedOrderPage";
import LiveBroadcastsPage from "./LiveBroadcastsPage";
import DeliveryAndPaymentPage from "./DeliveryAndPaymentPage";
import DeliveryPage from "@admin/pages/UniquePageEditor/DeliveryPage";
import ReturnPage from "./ReturnPage";
import { updateUniquePage } from "@admin/domain/uniquePages/api";
import { PaymentPage } from "./PaymentPage";
import Editor from "@admin/components/Editor";
import { type UniquePageModel } from "@admin/domain/uniquePages/models/UniquePageModel";
import Input from "@admin/components/Input";
import MultiLanguages from "@admin/components/MultiLanguage";
import PublishedAtInput from "@admin/components/PublishedAtInput";
import Switch from "@admin/components/Switch";
import Select from "@admin/components/Select";
import PageSeoParamsEditor from "@admin/components/PageSeoParamsEditor";
import AdditionalMaterials from "@admin/pages/UniquePageEditor/AdditionalInfo";
import PublicUrlInput from "@admin/components/PublicUrlInput";

export type UniquePageProps<T> = {
    page: UniquePageModel<T>;
};

export default function UniquePageEditor() {
    const pages = new Map<string, ({ page }: UniquePageProps<any>) => ReactNode>([
        [`main`, MainPage],
        [`about`, AboutPage],
        [`business`, BusinessPage],
        [`delivery_and_payment`, DeliveryAndPaymentPage],
        [`delivery`, DeliveryPage],
        [`payment`, PaymentPage],
        [`return`, ReturnPage],
        [`how_to_create_an_order`, HowCreateOrderPage],
        [`how_to_create_an_autoorder`, HowCreateAutoOrderPage],
        [`how_to_create_a_consolidated_order`, HowCreateConsolidatedOrderPage],
        [`live_broadcasts`, LiveBroadcastsPage],
        [`business_expansion`, BusinessExpansionPage],
        [`additional_materials`, AdditionalMaterials],
    ]);

    const page = window.CMS.uniquePage;

    if (!page) {
        throw new Error(`unique page not found`);
    }

    page.textsMap = new Map(page.texts.map((value) => [value.language, value]));
    page.contentObject = Object.fromEntries(page.contents.map((content) => [content.code, content.value]));

    const children = pages.get(page.code);

    async function execute(body: FormData) {
        await updateUniquePage((page as UniquePageModel<any>).id, body);
    }

    return (
        <Editor backUrl={`/admin/unique_pages/${page.id}`} onSubmit={execute}>
            <Switch label="Скрыть / Показать" name="is_public" defaultChecked={page?.is_public} />

            <Input label="Код страницы" defaultValue={page.code} disabled />
            <PublicUrlInput defaultValue={page.url} publicUrl={page.public_url} readOnly />
            <p className="form-text mb-5">
                URL этой страницы закреплён и не зависит от заголовка. Заголовок можно менять без изменения ссылки.
            </p>

            <MultiLanguages
                languageElement={(language) => (
                    <>
                        <input type="hidden" name={`texts[${language}][language]`} defaultValue={language} />

                        <Input
                            label="Заголовок страницы"
                            name={`texts[${language}][title]`}
                            defaultValue={page?.textsMap.get(language)?.title}
                        />
                    </>
                )}
            />

            <Select
                label="Автор"
                name="author_id"
                defaultValue={page?.author?.id}
                disabled={!page}
                options={(page?.authors || []).map((author) => ({
                    value: author.id as number,
                    label: `${author.name} ${author.email}`,
                }))}
                required
            />

            <PublishedAtInput defaultValue={page.published_at} />

            <PageSeoParamsEditor />

            {children && children({ page })}
        </Editor>
    );
}
